import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notifications from localStorage on mount
  useEffect(() => {
    const savedNotifications = localStorage.getItem('quavrion-notifications');
    if (savedNotifications) {
      try {
        const parsed = JSON.parse(savedNotifications);
        setNotifications(parsed);
        updateUnreadCount(parsed);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    }
  }, []);

  // Save notifications to localStorage
  useEffect(() => {
    localStorage.setItem('quavrion-notifications', JSON.stringify(notifications));
    updateUnreadCount(notifications);
  }, [notifications]);

  const updateUnreadCount = (notificationsList) => {
    const count = notificationsList.filter(n => !n.read).length;
    setUnreadCount(count);
    
    // Update browser tab title if there are unread notifications
    if (count > 0) {
      document.title = `(${count}) QUAVRION`;
    } else {
      document.title = 'QUAVRION';
    }
  };

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: notification.title,
      message: notification.message,
      type: notification.type || 'info',
      timestamp: new Date(),
      read: false,
      action: notification.action,
      icon: notification.icon,
      priority: notification.priority || 'normal'
    };

    setNotifications(prev => [newNotification, ...prev]);

    // Show browser notification if allowed
    if (Notification.permission === 'granted' && newNotification.priority === 'high') {
      new window.Notification(newNotification.title, {
        body: newNotification.message,
        icon: '/assets/images/logos/quavrion-logo.svg'
      });
    }

    return newNotification.id;
  }, []);

  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, read: true }))
    );
  }, []);

  const removeNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const getNotifications = useCallback((type = 'all', limit = null) => {
    let filtered = notifications;
    
    if (type === 'unread') {
      filtered = notifications.filter(n => !n.read);
    } else if (type === 'read') {
      filtered = notifications.filter(n => n.read);
    } else if (type !== 'all') {
      filtered = notifications.filter(n => n.type === type);
    }
    
    if (limit) {
      filtered = filtered.slice(0, limit);
    }
    
    return filtered;
  }, [notifications]);

  // Notification types helper
  const notify = {
    info: (title, message, options = {}) => 
      addNotification({ title, message, type: 'info', ...options }),
    
    success: (title, message, options = {}) => 
      addNotification({ title, message, type: 'success', ...options }),
    
    warning: (title, message, options = {}) => 
      addNotification({ title, message, type: 'warning', ...options }),
    
    error: (title, message, options = {}) => 
      addNotification({ title, message, type: 'error', ...options }),
    
    system: (title, message, options = {}) => 
      addNotification({ title, message, type: 'system', ...options })
  };

  // Request notification permission
  const requestPermission = useCallback(() => {
    if ('Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          notify.success(
            'Notifications Enabled',
            'You will now receive important updates from QUAVRION.',
            { priority: 'high' }
          );
        }
      });
    }
  }, [notify]);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    const autoDismiss = () => {
      const now = new Date();
      setNotifications(prev =>
        prev.filter(notif => {
          const age = now - new Date(notif.timestamp);
          // Keep high priority notifications longer
          if (notif.priority === 'high') return age < 30000; // 30 seconds
          if (notif.priority === 'normal') return age < 10000; // 10 seconds
          return age < 5000; // 5 seconds
        })
      );
    };

    const interval = setInterval(autoDismiss, 1000);
    return () => clearInterval(interval);
  }, []);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    getNotifications,
    notify,
    requestPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};